import {
  Msg,
  AVATAR_COLORS,
  WORLD,
  ROOMS,
  ROOM_LIST,
  facingFromIndex,
  facingFromVector,
  gestureFromIndex,
  doorsOf,
  elementContains,
  elementDepths,
  elementOutline,
  ELEMENT_TRAITS,
  artKeyOf,
  isOpenRoom,
  silentDoorAt,
  getClub,
  getRoom,
  type AnyRoom,
  type Door,
  type ElementDef,
  type ElementOf,
  type Facing,
  type Gesture,
  type MovePayload,
  type Player,
  type Point,
  type Rect,
  type RoomId,
  type RoomState,
  type UseElementPayload,
} from "@clubap/shared";
import { getStateCallbacks, type Room } from "colyseus.js";
import { Application, Container, Graphics, Polygon, Sprite, Text } from "pixi.js";
import { loadArt, type LoadedArt } from "../art/loader.js";
import { proceduralScene } from "../art/procedural/index.js";
import { loadEditorSession, saveEditorSession } from "../editor/persist.js";
import { AVATAR_HEIGHT, createAvatar, setBubble, type AvatarView } from "./avatar.js";
import type { EditorOverlay } from "./editor.js";

export interface PlayerSelection {
  sessionId: string;
  nickname: string;
  color: number;
  /**
   * La cuenta detrás del avatar. Es lo que hace posible mandarle una solicitud de
   * amistad: `sessionId` cambia en cada conexión y no identifica a nadie mañana.
   */
  accountId: string;
  /** Ids de club, para las insignias. Salen del estado sincronizado. */
  clubs: string[];
  /**
   * Lo que lleva puesto, para dibujar su figura en la tarjeta.
   *
   * Entra en la firma del roster en `App.tsx`, así que una tarjeta abierta se repinta
   * cuando el otro se cambia de sombrero — igual que ya pasaba al entrar a un club.
   */
  equipped: string[];
}

export interface RoomStageOptions {
  /** Clic sobre un avatar: abre su tarjeta de perfil. */
  onSelectPlayer?: (selection: PlayerSelection) => void;
  /**
   * Cambió lo que lleva puesto el jugador local.
   *
   * Sale de acá y no de un callback de Colyseus porque `onChange` de una colección
   * NO es recursivo: no se dispara cuando muta el array `equipped` de un jugador.
   * El bucle de render ya compara los arrays cada fotograma sobre el estado ya
   * asentado, así que es la fuente más confiable.
   */
  onSelfEquipped?: (itemIds: string[]) => void;
  /** El editor cambió de estado: la UI de formularios se redibuja. */
  onEditorChange?: (editor: EditorOverlay) => void;
  /**
   * Algo del mundo tiene que decirle una línea al jugador.
   *
   * Existe por el contenedor de un club ajeno: el servidor lo rechaza, pero un clic que
   * no hace nada no se distingue de un juego roto. El stage no sabe pintar avisos —eso
   * es DOM— así que los sube.
   */
  onNotice?: (text: string) => void;
  /** Cuenta de desarrollo: enciende los rótulos de diagnóstico sobre los avatares. */
  dev?: boolean;
  /**
   * Se levantó un mueble ya puesto, decorando.
   *
   * Devuelve si el punto vale: lo decide quien conoce las reglas (`placementProblem`),
   * no el stage, que sólo sabe pintar. Con eso la marca de aterrizaje se colorea sola
   * mientras se arrastra.
   */
  onDragValidate?: (rect: Rect, placementId: string) => boolean;
  /**
   * Se soltó el mueble. Las coordenadas de PANTALLA van además de las de mundo porque
   * soltar sobre el basurero —que es DOM, no canvas— se resuelve mirando qué elemento
   * hay debajo del cursor.
   */
  onPieceDropped?: (drop: {
    placementId: string;
    x: number;
    y: number;
    clientX: number;
    clientY: number;
  }) => void;
}

/** El paso de la rejilla del modo decorar, en unidades de mundo. */
const GRID_STEP = 40;

/** Qué tan rápido la posición dibujada persigue a la del servidor, por segundo. */
const SMOOTHING = 14;

const BUBBLE_MS = 4500;

/*
 * El vaiven de quien camina sin arte de caminata propio.
 *
 * Es el truco de Club Penguin: no hay piernas que animar, sólo un balanceo lateral
 * a ritmo FIJO. Sin pies que pisen el suelo, no hay nada que pueda desincronizarse
 * con la velocidad — el patinaje que delata a los ciclos de caminata baratos no
 * puede ocurrir.
 */
const BOB_SPEED = 12;
const BOB_HEIGHT = 3;
const BOB_TILT = 0.05;

/*
 * Cómo se mueve el cuerpo en cada gesto.
 *
 * Vive ACÁ y no en el catálogo a propósito: qué gestos hay es dato compartido, pero
 * cómo se ven es presentación, igual que el arte de una sala no vive en `ROOMS`.
 *
 * Amplitud y velocidad por separado para poder ajustarlas mirándolas: si algo parece un
 * tic nervioso, se baja la velocidad antes que la amplitud.
 */
const GESTURE_MOTION: Record<
  Exclude<Gesture, "ninguno">,
  { lift: number; liftSpeed: number; tilt: number; tiltSpeed: number }
> = {
  // Inclinación lateral rápida, sin salto: es un brazo, no un salto.
  saludo: { lift: 0, liftSpeed: 0, tilt: 0.22, tiltSpeed: 12 },
  // Rebote con vaivén, y el vaivén MÁS LENTO que el rebote: dos brincos por bamboleo.
  baile: { lift: 6, liftSpeed: 6, tilt: 0.18, tiltSpeed: 3 },
  // Temblor corto y rápido, de poca amplitud: sacude, no desplaza.
  risa: { lift: 2.5, liftSpeed: 14, tilt: 0.06, tiltSpeed: 14 },
  // Un salto seco, sin inclinación: lo que hace un cuerpo que se sobresalta.
  sorpresa: { lift: 9, liftSpeed: 5, tilt: 0, tiltSpeed: 0 },
  /*
   * Rebote corto y regular, casi sin inclinación. Es lo que lo separa de `risa`, que
   * con valores parecidos sería la misma animación con otro glifo: la risa SACUDE
   * —rápida y torcida— y el aplauso lleva un compás, derecho y más lento.
   */
  aplauso: { lift: 3, liftSpeed: 8, tilt: 0.02, tiltSpeed: 8 },
  /*
   * Lentísimo a propósito: es una respiración, no un movimiento. Las velocidades por
   * debajo de 1.5 son las únicas de la tabla que se leen como quietud en vez de como
   * animación, que es justo lo que tiene que parecer alguien dormido de pie.
   */
  siesta: { lift: 1.5, liftSpeed: 1.4, tilt: 0.07, tiltSpeed: 0.9 },
};

/** Radio en el que el cursor está "encima" del avatar y no marca dirección. */
const FACING_DEADZONE = 26;

/** Duración del anillo que confirma el clic, en segundos. */
const CLICK_MARKER_SECONDS = 0.45;

/**
 * Profundidad de la capa de nombres y globos.
 *
 * Por encima de cualquier mueble: el `zIndex` de un objeto es su borde inferior, así
 * que el techo real es la altura del mundo. Un número muy por arriba de eso deja claro
 * que no compite con nada, en vez de invitar a alguien a subir un mueble un poco más.
 *
 * Y por DEBAJO del editor, que está en 100_000 (`game/editor.ts`). Empatar con él
 * dejaba el orden en manos de quién se agregó primero al mundo, que es un empate que se
 * rompe solo el día que alguien mueva una línea de sitio: midiendo el mapa, los
 * manejadores tienen que estar encima de los rótulos.
 */
const NAMEPLATE_LAYER = 90_000;

interface Avatar {
  view: AvatarView;
  /** Estado autoritativo; sus campos mutan cuando llegan los patches. */
  player: Player;
  renderX: number;
  renderY: number;
  bob: number;
  /** Altura dibujada, interpolada hacia la de la superficie donde está. */
  lift: number;
  /** Rótulo de diagnóstico, sólo en cuentas dev. Se crea al hacer falta. */
  state: Text | null;
  /** Amplitud del balanceo, 1 caminando y bajando a 0 al parar. */
  sway: number;
  /** Lo último que se le dibujó puesto, para detectar cambios sin callbacks. */
  equipped: string[];
  /** El último gesto dibujado, para detectar el cambio sin callbacks. */
  gesture: Gesture;
  bubbleTimer?: ReturnType<typeof setTimeout>;
}

/** Igualdad de listas cortas, en orden. El servidor ya las manda ordenadas. */
function sameItems(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Dueño del canvas de Pixi para UNA sala. Se construye a partir de `RoomDef`, así
 * que sirve para cualquier lugar del campus sin subclases. No sabe nada de React.
 */
export class RoomStage {
  private app = new Application();
  private world = new Container();
  /**
   * Nombres y globos, en una capa por encima del mobiliario.
   *
   * Existe porque lo que dice la gente no es parte de la escena: el avatar se ordena
   * por sus pies para poder pasar por detrás de un mueble, pero su globo flota sobre
   * todo y no debería desaparecer detrás de un mesón. Separar las dos cosas es la única
   * forma de tener las dos a la vez con un solo criterio de profundidad.
   *
   * `eventMode = "none"` la saca del hit test entera: es decorado, y un globo que se
   * traga el clic de caminar sería exactamente el defecto que esto viene a arreglar.
   */
  private nameplates = new Container();
  private avatars = new Map<string, Avatar>();
  /** Los muebles dibujados, por id, con la firma de lo que ya se pintó. */
  private propViews = new Map<string, { view: Container; signature: string }>();
  /**
   * El editor de mapas, sólo donde puede existir. Ver `openEditor`.
   *
   * Nulo es el caso NORMAL —producción, y cualquier cuenta que no sea dev— así que
   * todo lo que lo toca pregunta primero. `editing` es esa pregunta, escrita una vez.
   */
  private editor: EditorOverlay | null = null;

  /** Qué hacer con el próximo clic en el mundo, si no es caminar. Ver `setPlacing`. */
  private placing: ((point: Point) => void) | null = null;

  /**
   * Modo decorar: los muebles se arrastran en vez de usarse.
   *
   * Mientras está encendido, tocar una silla la levanta para moverla en vez de sentarte
   * en ella. Son dos gestos sobre el mismo objeto, y sin un modo habría que inventar uno
   * distinto para cada cosa —doble clic, clic derecho— que en un teléfono no existe.
   */
  private decorating = false;

  /** El mueble que se está arrastrando por el canvas, con el agarre dentro de su caja. */
  private dragging: { id: string; grabX: number; grabY: number; rect: Rect } | null = null;

  /** La marca de aterrizaje: dónde caería y si vale. Se dibuja sobre el piso. */
  private dropMark = new Graphics();

  /** La rejilla del modo decorar. Ver `setGrid`. */
  private gridMark = new Graphics();
  private art: LoadedArt = {
    backgrounds: new Map(),
    avatarLayers: null,
    itemLayers: new Map(),
    props: new Map(),
    games: new Map(),
    portrait: null,
  };

  private get dev(): boolean {
    return this.options.dev === true;
  }

  /** ¿Se está midiendo en vez de jugando? Sin editor montado, nunca. */
  private get editing(): boolean {
    return this.editor?.active === true;
  }

  /** Flanco de caminata, para disparar las puertas ocultas al llegar. */
  private wasWalking = false;
  private cursor: Point | null = null;

  /**
   * Anillo que confirma el clic. El movimiento es autoritativo, así que entre el
   * clic y el primer paso pasa un viaje de ida y vuelta al servidor: sin esta
   * señal, con latencia el juego se siente roto.
   */
  private background: Container = new Container();

  private clickMarker = new Graphics();
  private clickAt: Point = { x: 0, y: 0 };
  private clickAge = Number.POSITIVE_INFINITY;

  private resizeObserver?: ResizeObserver;
  private onKeyDown = (event: KeyboardEvent) => {
    /*
     * Sin editor montado no hay atajos, y ni siquiera se le quita el F2 al navegador.
     *
     * El listener sólo se registra si hay editor, así que esto es cinturón sobre
     * tirantes — pero es la guarda que hace evidente que la tecla no hace nada donde
     * el editor no existe, que es todo el juego menos una cuenta dev en desarrollo.
     */
    if (!this.editor) return;

    // Nada de atajos mientras se escribe en un campo: "F2" no se teclea, pero
    // Ctrl+Z y Escape sí tienen significado dentro de un input.
    const target = document.activeElement?.tagName;
    const typing = target === "INPUT" || target === "TEXTAREA" || target === "SELECT";

    if (event.key === "F2") {
      event.preventDefault();
      this.editor.toggle();

      /* Al salir del modo edición se vuelve al escenario de la sala donde se está
         parado, que puede no ser la que se estaba editando. */
      const real = this.salaReal;
      if (real && !this.editor.active && this.def.id !== real.id) this.setRoom(real);
      return;
    }

    if (!this.editor.active || typing) return;

    if (event.key.toLowerCase() === "z" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.editor.undo();
      return;
    }

    /*
     * Supr quita lo seleccionado. Es lo que uno teclea por reflejo tras colocar algo
     * por error, y buscar el botón para eso rompe el ritmo de colocar y ajustar.
     *
     * La guarda de `typing` de arriba ya lo protege: dentro de un campo de texto,
     * Supr borra caracteres y no muebles.
     */
    if (event.key === "Delete" || event.key === "Backspace") {
      if (!this.editor.selectedElement) return;
      event.preventDefault();
      this.editor.removeSelected();
    }
  };

  /**
   * La profundidad con la que se DIBUJA cada objeto, según la pila de la sala.
   *
   * Se guarda en vez de recalcularse en cada sitio que la necesita, y eso no es una
   * optimización: es que había dos cuentas distintas para lo mismo. `frontOf` seguía
   * derivando la profundidad del borde inferior mientras los objetos ya se dibujaban
   * con la que reparte la pila, así que a un jugador sentado en una silla que hubiera
   * subido en la pila lo tapaba su propia silla. Una sola fuente y no puede volver a
   * discrepar.
   */
  private depths = new Map<string, number>();

  private destroyed = false;

  private constructor(
    private container: HTMLElement,
    private room: Room<RoomState>,
    /* `AnyRoom` y no `RoomDef`: acá también se dibuja el iglú de un jugador, que no
       está en el catálogo y por eso no tiene un `RoomId`. Lo que se pinta sale de los
       mismos cinco campos. */
    private def: AnyRoom,
    private options: RoomStageOptions,
  ) {}

  static async create(
    container: HTMLElement,
    room: Room<RoomState>,
    def: AnyRoom,
    options: RoomStageOptions = {},
  ): Promise<RoomStage> {
    const stage = new RoomStage(container, room, def, options);
    await stage.init();
    return stage;
  }

  private async init(): Promise<void> {
    this.art = await loadArt();

    await this.app.init({
      background: 0x0d2438,
      antialias: true,
      resizeTo: this.container,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
    });

    // create() puede resolverse después de que el llamador se desmontó.
    if (this.destroyed) {
      this.app.destroy({ removeView: true, releaseGlobalResources: false });
      return;
    }

    this.container.appendChild(this.app.canvas);

    this.world.sortableChildren = true;
    this.background = this.buildBackground();
    this.world.addChild(this.background);

    // Marca en el piso: debe quedar bajo los avatares, sobre el fondo.
    this.clickMarker.zIndex = 0;
    this.world.addChild(this.clickMarker);

    /* La sombra de aterrizaje va sobre el piso y bajo los muebles: es una marca EN el
       suelo, así que un mueble delante tiene que taparla. */
    this.dropMark.zIndex = 1;
    this.dropMark.eventMode = "none";
    this.world.addChild(this.dropMark);

    // La rejilla va debajo de la sombra de aterrizaje: es papel cuadriculado, no marca.
    this.gridMark.zIndex = 0;
    this.gridMark.eventMode = "none";
    this.world.addChild(this.gridMark);

    /* La pila de la sala: el orden de `elements` decide quién tapa a quién, y los
       valores son los bordes inferiores de siempre. Ver `elementDepths`. */
    const depths = elementDepths(this.def.elements);
    this.depths = depths;

    for (const door of doorsOf(this.def).filter((door) => this.canUse(door))) {
      this.world.addChild(this.buildDoor(door, depths.get(door.id) ?? 0));
    }

    this.syncProps();

    this.nameplates.zIndex = NAMEPLATE_LAYER;
    this.nameplates.eventMode = "none";
    // Entre rótulos manda lo mismo que entre avatares: quien está más adelante, encima.
    this.nameplates.sortableChildren = true;
    this.world.addChild(this.nameplates);

    await this.openEditor();
    if (this.destroyed) return;

    this.app.stage.addChild(this.world);

    this.bindInput();
    this.bindState();

    this.app.ticker.add(() => this.tick(this.app.ticker.deltaMS));

    this.layout();

    /*
     * Se redimensiona el RENDERER además de recolocar el mundo.
     *
     * `resizeTo` sólo se engancha a `window.resize` —está así en la implementación de
     * Pixi 8, no es una suposición— y esto observa el CONTENEDOR, que es otra cosa: la
     * franja del navegador que se retrae, un panel que se abre, la emulación de móvil de
     * las devtools. En todos esos casos `.stage` cambia de tamaño sin que la ventana lo
     * haga.
     *
     * Sin el `resize`, el canvas se quedaba con el tamaño viejo y por el hueco se veía
     * el fondo de la página: una banda azul oscuro a un lado del mundo que parecía sitio
     * libre y era un canvas que no había crecido. `layout()` encima leía un
     * `app.screen` viejo, así que centraba contra un tamaño que ya no existía.
     */
    this.resizeObserver = new ResizeObserver(() => {
      this.app.resize();
      this.layout();
    });
    this.resizeObserver.observe(this.container);

    // Los atajos son del editor y de nadie más: sin editor no hay nada que escuchar.
    if (this.editor) window.addEventListener("keydown", this.onKeyDown);
  }

  /**
   * Monta el editor de mapas, si esta build y esta cuenta pueden tenerlo.
   *
   * Las dos condiciones son las mismas que ya exigía su panel de React, y ninguna
   * sobra. `import.meta.env.DEV` porque el endpoint que reescribe `rooms.ts` es un
   * plugin de Vite que no existe en un build de producción. Y la cuenta dev porque en
   * un servidor de desarrollo compartido cualquiera podría reescribir el mapa de todos.
   *
   * Antes esto se construía siempre y sólo el PANEL estaba condicionado, así que en
   * producción F2 seguía abriendo la capa de edición sobre el canvas: se veía el
   * andamiaje del mapa, y como en modo edición los clics van al editor, quien la
   * abriera sin querer se quedaba sin poder caminar hasta volver a pulsarla.
   *
   * El import es dinámico para que el editor entero —que no es pequeño— tampoco entre
   * al bundle: `import.meta.env.DEV` es una constante en el build, así que la rama se
   * borra y el módulo se va con ella.
   */
  /**
   * La sala donde el jugador está DE VERDAD, mientras el editor previsualiza otra.
   *
   * Nula fuera del editor. Existe porque el editor puede repintar el escenario de
   * cualquier sala encima de ésta, y al cerrarlo hay que devolver el que corresponde al
   * sitio que el servidor está simulando — o se sale del modo edición mirando una sala
   * en la que no se está.
   */
  private salaReal: AnyRoom | null = null;

  private async openEditor(): Promise<void> {
    if (!import.meta.env.DEV || !this.dev) return;

    /*
     * Y sólo en una sala DEL CATÁLOGO.
     *
     * El editor reescribe `rooms.ts`, así que dentro de un iglú no tendría qué escribir:
     * su piso es una constante compartida y sus muebles son filas de una base. Abrirlo
     * ahí sería ofrecer un guardado que no puede existir — y de paso dejaría al jugador
     * en modo edición, donde los clics no caminan.
     */
    const catalogRoom = getRoom(this.def.id);
    if (!catalogRoom) return;

    const { EditorOverlay } = await import("./editor.js");
    if (this.destroyed) return;

    this.salaReal = this.def;

    const editor = new EditorOverlay(catalogRoom, ROOM_LIST, {
      /*
       * La proporción sale de la TEXTURA ya cargada, que es el archivo de verdad.
       *
       * El editor no toca Pixi ni conoce el manifest, así que la pide y la resuelve
       * quien sí las tiene en la mano. `ART_SHAPES` guarda una copia para que el
       * servidor pueda validar sin abrir imágenes, pero cuando discrepan la vieja es
       * la copia — y guardar desde el editor la pone al día.
       */
      naturalAspect: (artKey) => {
        const texture = this.art.props.get(artKey);
        if (!texture || texture.height === 0) return null;
        return texture.width / texture.height;
      },
      onRoomLoaded: (room) => {
        this.setRoom(room);
      },
      onChange: () => {
        // Se persiste en CADA cambio, no al cerrar: un reinicio del servidor no
        // avisa, asi que no hay un momento posterior donde guardar.
        saveEditorSession(editor.snapshot());
        // El mundo sigue al borrador mientras se edita, no al catálogo guardado.
        this.syncProps();
        this.options.onEditorChange?.(editor);
      },
    });

    this.editor = editor;
    this.world.addChild(editor.view);

    /*
     * Se restaura DESPUES de montar la vista: `restore` redibuja, y hacerlo sobre un
     * contenedor que todavia no cuelga del mundo dejaria el editor invisible aunque
     * su estado dijera que esta abierto.
     */
    const previous = loadEditorSession();
    if (previous) editor.restore(previous);
  }

  /** Imagen del manifest si existe; si no, el escenario procedural. */
  private buildBackground(room: AnyRoom = this.def): Container {
    const texture = this.art.backgrounds.get(room.id);

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.width = WORLD.width;
      sprite.height = WORLD.height;
      sprite.zIndex = -1;
      return sprite;
    }

    const scene = proceduralScene(room.art) ?? new Container();
    scene.zIndex = -1;
    return scene;
  }

  /**
   * ¿Esta puerta lleva a alguna parte?
   *
   * Una que da a una sala cerrada no se dibuja ni se dispara. El servidor la rechaza
   * igual, pero una zona que se ilumina al pasar el ratón y después no hace nada se
   * lee como un juego roto y no como una sala que todavía no está abierta.
   *
   * No hay excepción para las cuentas dev, y es deliberado: el campus se ve igual para
   * todos. Para trabajar en una sala cerrada se la abre un rato en `OPEN_ROOM_IDS`,
   * que además es la única forma de verla como la va a ver el jugador.
   */
  private canUse(door: Door): boolean {
    return isOpenRoom(door.to);
  }

  /**
   * Cartel de puerta, generado desde el catálogo. Así cada sala nueva tiene
   * puertas visibles y clickeables sin trabajo de arte.
   */
  private buildDoor(door: Door, depth: number): Container {
    const group = new Container();
    // El borde frontal de la zona: un avatar delante de la puerta la tapa.
    group.zIndex = depth;

    /*
     * La silueta sale de `elementOutline`, el mismo contorno con el que el servidor
     * decide si estás parado dentro. Dibujar por separado -- un roundRect acá y otra
     * fórmula allá -- es como se acaba con puertas que se ven pero no se cruzan.
     */
    const floor = new Graphics();
    floor
      .poly(elementOutline(door))
      .fill({ color: 0xffffff, alpha: 0.14 })
      .stroke({ width: 2, color: 0xffffff, alpha: 0.35 });
    // Base atenuada para que el hover tenga a dónde subir; alpha se satura en 1.
    floor.alpha = 0.7;

    /*
     * Una puerta oculta no dibuja nada y no responde al puntero: seria un rectangulo
     * invisible que se traga clics destinados al piso. Se cruza caminando dentro,
     * que es lo que la hace util como pasaje secreto.
     */
    if (!door.hidden) group.addChild(floor);

    if (!door.hidden && door.showLabel !== false) group.addChild(this.buildSign(door.rect, door.label));

    if (door.hidden) return group;

    group.eventMode = "static";
    group.cursor = "pointer";

    group.on("pointerover", () => {
      floor.alpha = 1;
    });
    group.on("pointerout", () => {
      floor.alpha = 0.7;
    });

    group.on("pointerdown", (event) => {
      // El editor tiene prioridad: en modo edición se mide, no se juega.
      if (this.editing) return;
      /* Y colocar la tiene sobre usar: decorando, un clic cerca del umbral o de otro
         mueble deja la pieza ahí en vez de cruzar o sentarse. El clic sigue su camino
         hasta el stage, que es quien coloca. */
      if (this.placing) return;

      event.stopPropagation();

      /*
       * Se manda la INTENCION, no un destino.
       *
       * Antes esto caminaba solo hasta el centro y disparaba la puerta al llegar,
       * lo que dejaba al cliente decidiendo cuándo había llegado — y un cliente
       * modificado se cruzaba desde cualquier sitio. Ahora el servidor calcula el
       * punto de aproximación, te lleva y resuelve.
       *
       * `at` es QUÉ PARTE se señaló, y sigue siendo intención: el servidor lo recorta
       * al contorno de esta misma puerta antes de mirarlo. Sin él, un clic en el
       * extremo derecho de una puerta ancha y uno en el izquierdo eran el mismo
       * mensaje, y los dos acababan caminando a la misma esquina.
       */
      const donde = this.world.toLocal(event.global);
      this.room.send(Msg.UseElement, {
        elementId: door.id,
        at: { x: donde.x, y: donde.y },
      } satisfies UseElementPayload);
      this.markClick(donde);
    });

    return group;
  }

  /**
   * El cartel sobre una zona: centrado encima de su caja.
   *
   * Lo comparten las puertas y la cancha para que se lean como la misma familia de
   * cosas — sitios a los que se va — y no como dos estilos de letrero.
   */
  private buildSign(rect: Rect, text: string): Text {
    const label = new Text({
      text,
      style: {
        fontFamily: "Verdana, Geneva, sans-serif",
        fontSize: 12,
        fontWeight: "bold",
        fill: 0xffffff,
        stroke: { color: 0x1b3f6b, width: 4 },
      },
    });
    label.anchor.set(0.5, 1);
    label.x = rect.x + rect.width / 2;
    label.y = rect.y - 4;
    return label;
  }

  private bindInput(): void {
    this.app.stage.eventMode = "static";
    this.app.stage.hitArea = this.app.screen;

    this.app.stage.on("pointermove", (event) => {
      this.cursor = this.world.toLocal(event.global);
      if (this.editing) this.editor?.handlePointerMove(this.cursor);
      if (this.dragging) this.dragTo(this.cursor);
    });

    this.app.stage.on("pointerup", (event) => {
      if (this.editing) this.editor?.handlePointerUp();
      if (this.dragging) this.dropDragged(event.client.x, event.client.y);
    });
    /*
     * Soltar FUERA del canvas cuenta como soltar.
     *
     * Es el caso que importa acá: el basurero y la barra son DOM y están encima del
     * mundo, así que arrastrar un mueble hasta ellos termina el gesto fuera de Pixi. Sin
     * esto, la pieza se quedaba pegada al cursor para siempre.
     */
    this.app.stage.on("pointerupoutside", (event) => {
      if (this.editing) this.editor?.handlePointerUp();
      if (this.dragging) this.dropDragged(event.client.x, event.client.y);
    });

    // Sin esto, el clic derecho para borrar un vértice abriría el menú del navegador.
    this.app.canvas.addEventListener("contextmenu", (event) => {
      if (this.editing) event.preventDefault();
    });

    this.app.stage.on("pointerdown", (event) => {
      const local = this.world.toLocal(event.global);

      if (this.editing) {
        this.editor?.handlePointerDown(local, event.button);
        return;
      }

      /*
       * Decorando, el clic COLOCA en vez de caminar.
       *
       * Es de un solo uso: se pide un punto, se entrega y se vuelve a caminar. Así
       * decorar no necesita un modo del que haya que acordarse de salir, que es como el
       * editor se ganó su propio botón de pánico.
       */
      const place = this.placing;
      if (place) {
        this.setPlacing(null);
        place(local);
        return;
      }

      this.room.send(Msg.Move, { x: local.x, y: local.y } satisfies MovePayload);
      this.markClick(local);
    });
  }

  /**
   * Pide un punto del mundo para el próximo clic, en vez de caminar hacia él.
   *
   * Es como se decora el iglú sin construir un arrastre entero sobre el canvas: la UI
   * pide "¿dónde?", el mundo contesta con coordenadas de MUNDO —traducirlas desde la
   * pantalla es justo lo que este stage sabe hacer y React no— y el panel se encarga del
   * resto. `null` cancela.
   */
  setPlacing(handler: ((point: Point) => void) | null): void {
    this.placing = handler;
    this.app.canvas.style.cursor = handler ? "crosshair" : "";
  }

  /**
   * La rejilla del piso, mientras se decora.
   *
   * Hace dos cosas, y la segunda es la que la justifica: ayuda a alinear muebles, y
   * sobre todo **enseña dónde acaba el piso**. En un cuarto cuyo suelo es un polígono
   * invisible, esa frontera sólo se descubre chocando — o soltando una pieza que rebota.
   */
  setGrid(on: boolean): void {
    this.gridMark.clear();
    if (!on) return;

    const xs = this.def.walkable.map((point) => point.x);
    const ys = this.def.walkable.map((point) => point.y);
    const left = Math.min(...xs), right = Math.max(...xs);
    const top = Math.min(...ys), bottom = Math.max(...ys);

    for (let x = left; x <= right; x += GRID_STEP) {
      this.gridMark.moveTo(x, top).lineTo(x, bottom);
    }
    for (let y = top; y <= bottom; y += GRID_STEP) {
      this.gridMark.moveTo(left, y).lineTo(right, y);
    }

    this.gridMark.stroke({ width: 1, color: 0xffffff, alpha: 0.45 });
    this.gridMark
      .poly(this.def.walkable)
      .stroke({ width: 2, color: 0xffffff, alpha: 0.7 });
  }

  /**
   * Enciende o apaga el modo decorar.
   *
   * Al apagarlo se limpia lo que quede a medias: una marca de aterrizaje o un mueble
   * levantado que nunca se soltó. Sin esto, salir del modo con algo en la mano dejaba la
   * pieza flotando en su sitio provisional hasta recargar.
   */
  setDecorating(on: boolean): void {
    this.decorating = on;
    if (!on) {
      this.dragging = null;
      this.showDrop(null, false);
      this.syncProps();
    }
  }

  /**
   * De coordenadas de PANTALLA a coordenadas de MUNDO.
   *
   * El mundo se escala y se centra con letterbox (`layout`), así que un punto del ratón
   * no vale nada hasta deshacer esas dos cosas. Vive acá porque acá está la transformada;
   * que React la recalculara por su cuenta sería una segunda fórmula esperando a
   * separarse de la primera.
   */
  worldAt(clientX: number, clientY: number): Point {
    const rect = this.app.canvas.getBoundingClientRect();
    const scale = this.world.scale.x || 1;

    return {
      x: (clientX - rect.left - this.world.x) / scale,
      y: (clientY - rect.top - this.world.y) / scale,
    };
  }

  /**
   * Pinta dónde caería la pieza, y de qué color.
   *
   * La sombra va en el SUELO y no bajo el cursor: la pieza que se arrastra se dibuja
   * levantada, así que sin una marca pegada al piso no hay forma de saber dónde va a
   * aterrizar de verdad. Verde vale, rojo no — el motivo lo dice la UI, acá sólo se
   * responde sí o no.
   */
  showDrop(rect: Rect | null, valid: boolean): void {
    this.dropMark.clear();
    if (!rect) return;

    const color = valid ? 0x3f8f4f : 0xe3453c;
    const base = { x: rect.x + rect.width / 2, y: rect.y + rect.height };

    this.dropMark
      .ellipse(base.x, base.y - 6, rect.width / 2, Math.max(10, rect.height / 4))
      .fill({ color, alpha: 0.28 })
      .stroke({ width: 3, color, alpha: 0.9 });
  }

  /**
   * Cambia la sala que se dibuja sin rehacer el canvas.
   *
   * Los muebles de un iglú se ponen y se quitan estando dentro, y volver a montar el
   * stage por mover una silla apagaría el mundo entero —avatares incluidos— en cada
   * cambio. `syncProps` ya reconstruye sólo lo que cambió.
   *
   * Las puertas no se tocan: se montan al entrar, y la del iglú es siempre la misma.
   */
  setRoom(def: AnyRoom): void {
    const repintarFondo = def.art !== this.def.art;
    this.def = def;

    /*
     * El fondo sólo se rehace si CAMBIÓ de clave.
     *
     * Cambiar el piso o la pared cambia la clave de arte, así que hay que volver a
     * construirlo; mover una silla no, y rehacerlo en cada arrastre sería tirar y
     * reconstruir un escenario entero sesenta veces por segundo.
     */
    if (repintarFondo) {
      this.background?.destroy({ children: true });
      this.background = this.buildBackground();
      this.world.addChild(this.background);
    }

    this.syncProps();
  }

  private bindState(): void {
    const $ = getStateCallbacks(this.room);

    $(this.room.state).players.onAdd((player, sessionId) => {
      const color = AVATAR_COLORS[player.color] ?? AVATAR_COLORS[0];
      const view = createAvatar({
        color,
        nickname: player.nickname,
        layers: this.art.avatarLayers,
        itemLayers: this.art.itemLayers,
        isSelf: sessionId === this.room.sessionId,
      });

      // Lo que ya lleva puesto al aparecer: quien entra a la sala debe verle el
      // sombrero desde el primer fotograma, no al primer cambio.
      view.setEquipped([...player.equipped]);

      /*
       * Y el gesto que ya trae puesto, por lo mismo: quien entra mientras cuatro bailan
       * tiene que verlos bailando desde el primer fotograma. Es lo que justifica que el
       * gesto viva en el estado y no en un `broadcast`; sin esta línea, el diff del tick
       * no vería cambio alguno y el glifo no aparecería nunca.
       */
      view.setGesture(gestureFromIndex(player.gesture));

      // Y se emite el estado inicial: el tick sólo avisa de CAMBIOS, así que sin
      // esto la UI arrancaría creyendo que no llevas nada puesto.
      if (sessionId === this.room.sessionId) {
        this.options.onSelfEquipped?.([...player.equipped]);
      }

      view.root.x = player.x;
      view.root.y = player.y;

      // Clic sobre un jugador abre su perfil en vez de caminar hacia ahí, igual
      // que en Club Penguin.
      view.root.eventMode = "static";
      view.root.cursor = "pointer";
      view.root.on("pointerdown", (event) => {
        if (this.editing) return;
        event.stopPropagation();
        this.options.onSelectPlayer?.({
          sessionId,
          nickname: player.nickname,
          color: player.color,
          /* Se leen al ABRIR la tarjeta, no al crear el avatar: los clubes pueden
             cambiar durante la sesión y el estado ya trae el valor fresco. */
          accountId: player.accountId,
          clubs: [...player.clubs],
          equipped: [...player.equipped],
        });
      });

      this.world.addChild(view.root);
      this.nameplates.addChild(view.nameplate);
      this.avatars.set(sessionId, {
        view,
        player,
        renderX: player.x,
        renderY: player.y,
        bob: 0,
        lift: 0,
        state: null,
        sway: 0,
        equipped: [...player.equipped],
        gesture: gestureFromIndex(player.gesture),
      });
    });

    $(this.room.state).players.onRemove((_player, sessionId) => {
      const avatar = this.avatars.get(sessionId);
      if (!avatar) return;

      clearTimeout(avatar.bubbleTimer);
      avatar.view.root.destroy({ children: true });
      // El rótulo cuelga de otra capa: destruir la raíz ya no se lo lleva por delante.
      avatar.view.nameplate.destroy({ children: true });
      this.avatars.delete(sessionId);
    });
  }

  /** Letterbox del mundo de tamaño fijo dentro del espacio disponible. */
  private layout(): void {
    const { width, height } = this.app.screen;
    const scale = Math.min(width / WORLD.width, height / WORLD.height);

    this.world.scale.set(scale);
    this.world.x = (width - WORLD.width * scale) / 2;
    this.world.y = (height - WORLD.height * scale) / 2;

    this.app.stage.hitArea = this.app.screen;
  }

  private tick(deltaMs: number): void {
    const dt = deltaMs / 1000;
    const alpha = 1 - Math.exp(-SMOOTHING * dt);

    for (const [sessionId, avatar] of this.avatars) {
      const { player, view } = avatar;

      avatar.renderX += (player.x - avatar.renderX) * alpha;
      avatar.renderY += (player.y - avatar.renderY) * alpha;

      /*
       * La altura sube el SPRITE, no la posición.
       *
       * El jugador sigue estando en el suelo para todo lo demás — colisión,
       * interacción, el orden de dibujo — y sólo se dibuja más arriba. Mover su
       * posición real habría hecho que subirse a una silla lo sacara del piso
       * caminable, y con él la colisión entera.
       *
       * Se interpola igual que la posición: sin eso, subirse y bajarse serían saltos
       * de dieciséis píxeles en un fotograma.
       */
      const height = this.heightOf(player.onElement);
      avatar.lift += (height - avatar.lift) * alpha;

      this.updateStateLabel(avatar);

      view.root.x = avatar.renderX;
      view.root.y = avatar.renderY - avatar.lift;

      // El rótulo vive en otra capa, así que hay que llevarlo de la mano. Mismo punto
      // que la raíz: sus hijos siguen midiendo desde los pies, como cuando colgaban ahí.
      view.nameplate.x = view.root.x;
      view.nameplate.y = view.root.y;
      view.nameplate.zIndex = avatar.renderY;

      /*
       * La profundidad sale de los PIES, no del sprite.
       *
       * Restarle la altura al zIndex haría que quien está encima de una mesa se
       * dibujara detrás de quien está delante de ella, que es exactamente al revés de
       * lo que se ve en el mundo.
       *
       * La excepción es el objeto sobre el que estás: sentado en una silla, tus pies
       * quedan por encima del borde inferior del mueble, así que la regla general te
       * escondía detrás del respaldo. Encima de algo se dibuja siempre delante de ESO,
       * sin tocar la profundidad frente a todo lo demás.
       */
      view.root.zIndex = Math.max(avatar.renderY, this.frontOf(player.onElement));

      // La dirección viene del estado: la decide el servidor a partir del clic
      // crudo. Inferirla acá desde el destino recortado impedía girar al estar
      // pegado a un borde, y cada cliente podía llegar a una conclusión distinta.
      const facing = this.localFacing(sessionId, avatar) ?? facingFromIndex(player.facing);
      view.update(facing, player.walking, dt);

      // Se compara contra lo dibujado en vez de suscribirse al array: Colyseus
      // notifica cada alta y baja por separado, y una lista de dos elementos es
      // más barata de comparar que de orquestar con callbacks.
      if (!sameItems(avatar.equipped, player.equipped)) {
        avatar.equipped = [...player.equipped];
        view.setEquipped(avatar.equipped);

        if (player.sessionId === this.room.sessionId) {
          this.options.onSelfEquipped?.([...avatar.equipped]);
        }
      }

      /*
       * El gesto se diffea igual que lo equipado y por la misma razón: `onChange` de
       * una colección no es recursivo, así que nadie avisa de que un campo del `Player`
       * cambió. Comparar contra lo dibujado es más barato que orquestarlo con callbacks.
       */
      const gesture = gestureFromIndex(player.gesture);
      if (gesture !== avatar.gesture) {
        avatar.gesture = gesture;
        view.setGesture(gesture);
        // Desde cero, o el gesto entraría por la mitad de su ciclo y arrancaría de un tirón.
        avatar.bob = 0;
      }

      if (view.animated) {
        // El ciclo pre-renderizado ya mueve al personaje; sumarle el rebote
        // procedural se ve como si tuviera hipo — y eso vale igual para el gesto, que
        // el día que el arte traiga sus propios ciclos se apagará por aquí.
        view.body.y = 0;
        view.body.rotation = 0;
      } else if (player.walking) {
        avatar.bob += dt * BOB_SPEED;
        avatar.sway = 1;
        view.body.y = Math.abs(Math.sin(avatar.bob)) * -BOB_HEIGHT;
        view.body.rotation = Math.sin(avatar.bob) * BOB_TILT;
      } else if (gesture !== "ninguno") {
        /*
         * Caminar gana sobre el gesto porque el servidor ya los hace excluyentes: esta
         * rama sólo cubre el tick de latencia entre el clic y el patch que lo apaga.
         *
         * Y se apaga el balanceo de caminata: son el mismo contenedor, y un residuo del
         * vaivén sumado al gesto se ve como un tropiezo.
         */
        const motion = GESTURE_MOTION[gesture];

        avatar.bob += dt;
        avatar.sway = 0;

        view.body.y = -Math.abs(Math.sin(avatar.bob * motion.liftSpeed)) * motion.lift;
        view.body.rotation = Math.sin(avatar.bob * motion.tiltSpeed) * motion.tilt;
      } else if (avatar.sway > 0.01) {
        /*
         * El balanceo no se congela: sigue girando y se apaga.
         *
         * Antes `bob` volvía a cero en el mismo fotograma en que dejabas de caminar,
         * y lo único que quedaba era el residuo de Y y rotación derivando a neutro
         * desde donde estuviera. Eso es un frenazo, no una parada. Manteniendo la
         * fase viva y bajando la AMPLITUD, el vaivén se cierra solo — que es lo que
         * hacía Club Penguin: volver a la vertical en vez de cortar la zancada.
         */
        avatar.bob += dt * BOB_SPEED;
        avatar.sway += (0 - avatar.sway) * alpha;

        view.body.y = Math.abs(Math.sin(avatar.bob)) * -BOB_HEIGHT * avatar.sway;
        view.body.rotation = Math.sin(avatar.bob) * BOB_TILT * avatar.sway;
      } else {
        avatar.sway = 0;
        avatar.bob = 0;

        /*
         * Apagado del todo, pero no de un tirón.
         *
         * Un gesto termina donde lo pilla el servidor —caducó, o el jugador se subió a
         * algo— y eso puede ser a mitad de una inclinación de 0.22 radianes. Cortar ahí
         * es el mismo frenazo que ya se arregló para el balanceo: se deja caer a cero y
         * recién ahí se ancla, que es cuando se puede dejar de calcular.
         */
        if (Math.abs(view.body.y) > 0.05 || Math.abs(view.body.rotation) > 0.002) {
          view.body.y += (0 - view.body.y) * alpha;
          view.body.rotation += (0 - view.body.rotation) * alpha;
        } else {
          view.body.y = 0;
          view.body.rotation = 0;
        }
      }
    }

    this.editor?.update(this.cursor);
    this.updateClickMarker(dt);
    this.resolveSilentDoor();
  }

  /**
   * Tu avatar mira al cursor, sólo en tu pantalla.
   *
   * Es deliberadamente LOCAL: `facing` viaja en el estado sincronizado justamente
   * para que todos vean al mismo jugador mirando al mismo lado, y mandar cada
   * movimiento del ratón por la red serían decenas de mensajes por segundo por
   * jugador para algo que nadie más necesita. El precio, y hay que tenerlo claro, es
   * que mientras estás quieto los demás te ven mirando a donde el servidor cree —
   * hacia tu último clic. En cuanto te mueves, esa verdad vuelve a mandar.
   *
   * Sólo estando quieto: girar la cabeza a mitad de una caminata dejaría al avatar
   * andando de lado, que es justo el defecto que delata a un motor barato.
   */
  private localFacing(sessionId: string, avatar: Avatar): Facing | null {
    if (sessionId !== this.room.sessionId) return null;
    if (avatar.player.walking) return null;

    // En modo edición el cursor está midiendo, no jugando.
    if (this.editing) return null;

    const cursor = this.cursor;
    if (!cursor) return null;

    const dx = cursor.x - avatar.renderX;
    const dy = cursor.y - avatar.renderY;

    /*
     * Zona muerta alrededor del propio avatar.
     *
     * Con el cursor encima, el vector es de unos pocos píxeles y el ruido de un
     * píxel basta para saltar entre dos de las ocho direcciones: el avatar temblaría
     * en el sitio. Fuera del radio, la dirección es estable.
     */
    if (Math.hypot(dx, dy) < FACING_DEADZONE) return null;

    return facingFromVector(dx, dy);
  }

  /**
   * Pone los muebles al día con lo que hay que dibujar.
   *
   * Con el editor abierto la fuente es el BORRADOR, no el catálogo del servidor. Sin
   * esto, colocar una silla o arrastrarla no mostraba su dibujo hasta guardar y
   * esperar el reinicio: se editaba a ciegas sobre un contorno de colores, que es
   * justo lo que el editor existe para evitar.
   *
   * Se reconstruye sólo lo que cambió, comparando una firma barata. Un arrastre
   * cambia la geometría en cada fotograma, y aun así rehacer un `Sprite` sobre una
   * textura ya cargada no vuelve a decodificar nada.
   */
  private syncProps(): void {
    const source = this.editing && this.editor ? this.editor.current.elements : this.def.elements;
    /* Se recalcula cada pasada porque el editor reordena en caliente: la pila es el
       orden del array, así que mover un objeto cambia la profundidad de varios. */
    const depths = elementDepths(source);
    // El editor reordena en caliente, así que la pila vigente es la de ESTA pasada.
    this.depths = depths;
    const alive = new Set<string>();

    for (const element of source) {
      if (element.kind === "puerta") continue;
      alive.add(element.id);

      /* La profundidad entra en la firma: sin ella, reordenar la pila no reconstruiría
         nada y el cambio no se vería hasta recargar la sala. */
      const signature = JSON.stringify([
        element.rect,
        element.surface,
        artKeyOf(element),
        depths.get(element.id),
        // Marcarlo como pintado en el fondo cambia qué se dibuja, no dónde.
        element.backdrop === true,
      ]);
      const existing = this.propViews.get(element.id);
      if (existing?.signature === signature) continue;

      existing?.view.destroy({ children: true });

      const view = this.buildProp(element, depths.get(element.id) ?? 0);
      this.world.addChild(view);
      this.propViews.set(element.id, { view, signature });
    }

    for (const [id, entry] of this.propViews) {
      if (alive.has(id)) continue;
      entry.view.destroy({ children: true });
      this.propViews.delete(id);
    }
  }

  /**
   * Un mueble dibujado a mano alzada, mientras no tenga arte propio.
   *
   * La silueta se pinta con el mismo contorno que usa la colisión, así que lo que ves
   * es exactamente lo que estorba. Y la superficie se marca aparte, más clara: es la
   * única forma de entender de un vistazo por dónde se puede subir sin abrir el
   * editor.
   */
  private buildProp(element: ElementDef, depth: number): Container {
    if (element.kind === "directorio") return this.buildClubDirectory(element, depth);

    const group = new Container();
    const texture = this.art.props.get(artKeyOf(element));
    const outline = elementOutline(element);

    /*
     * Con arte declarado se dibuja el sprite sobre el rect; sin él, la silueta.
     *
     * El sprite se estira al rect y no al revés: `rect` es lo que el editor mueve y
     * lo que decide dónde se puede tocar, así que hacer mandar a la imagen dejaría el
     * dibujo y la zona de interacción diciendo cosas distintas.
     */
    const shape = new Graphics();

    /*
     * Opacidad en reposo del contorno. Uno pintado en el fondo no se ve hasta que el
     * ratón pasa encima: el dibujo ya está en la imagen, y lo único que falta decir es
     * "esto se puede tocar".
     */
    const restAlpha = element.backdrop ? 0 : 0.85;

    if (element.backdrop) {
      shape
        .poly(outline)
        .fill({ color: 0xffffff, alpha: 0.12 })
        .stroke({ width: 2, color: 0xffffff, alpha: 0.7 });

      if (element.surface) {
        shape.poly(element.surface.polygon).fill({ color: 0xffffff, alpha: 0.3 });
      }

      /*
       * La zona de clic va declarada, no deducida de lo que se dibuja.
       *
       * Con el contorno transparente, que el puntero lo encuentre dependería de cómo
       * Pixi trate un gráfico sin opacidad — y una grada que se ilumina pero no responde
       * se lee como un fallo. El polígono es el mismo que usa el servidor para el clic.
       */
      group.hitArea = new Polygon(outline);
    } else if (texture) {
      const sprite = new Sprite(texture);
      sprite.x = element.rect.x;
      sprite.y = element.rect.y;
      sprite.width = element.rect.width;
      sprite.height = element.rect.height;
      group.addChild(sprite);
    } else {
      shape
        .poly(outline)
        .fill({ color: 0x6b4b32, alpha: 0.9 })
        .stroke({ width: 2, color: 0x3c2a1c });

      if (element.surface) {
        shape
          .poly(element.surface.polygon)
          .fill({ color: 0x9a7250, alpha: 0.95 })
          .stroke({ width: 2, color: 0x3c2a1c });
      }
    }

    group.addChild(shape);

    /*
     * La profundidad sale del BORDE INFERIOR de su caja, no de su centro.
     *
     * Es lo que hace que pasar por detrás de un mueble funcione: quien tenga los pies
     * más abajo se dibuja delante, y el pie del mueble es su borde de abajo.
     */
    group.zIndex = depth;

    /*
     * La cancha lleva cartel, como una puerta.
     *
     * Un mostrador se lee como tienda, pero un balón en el piso se lee como un balón y
     * no como "aquí se juega contra alguien". El cartel dice lo que pasa al tocarlo, no
     * qué es: eso ya lo dice el dibujo.
     */
    if (element.kind === "cancha") group.addChild(this.buildSign(element.rect, "Jugar volley"));

    shape.alpha = restAlpha;

    // Decorado puro: no se toca, y no debe tragarse los clics del piso.
    if (!ELEMENT_TRAITS[element.kind].interactive) return group;

    group.eventMode = "static";
    group.cursor = "pointer";

    group.on("pointerover", () => {
      shape.alpha = 1;
    });
    group.on("pointerout", () => {
      shape.alpha = restAlpha;
    });

    group.on("pointerdown", (event) => {
      // El editor tiene prioridad: en modo edición se mide, no se juega.
      if (this.editing) return;
      /* Y colocar la tiene sobre usar: decorando, un clic cerca del umbral o de otro
         mueble deja la pieza ahí en vez de cruzar o sentarse. El clic sigue su camino
         hasta el stage, que es quien coloca. */
      if (this.placing) return;

      /*
       * Decorando, tocar un mueble lo LEVANTA en vez de usarlo.
       *
       * Se guarda dónde se agarró dentro de la caja para que la pieza no salte a
       * centrarse bajo el cursor: se arrastra por el punto que tocaste, que es lo que
       * hace que el gesto se sienta físico.
       */
      if (this.decorating) {
        event.stopPropagation();
        const point = this.world.toLocal(event.global);
        this.dragging = {
          id: element.id,
          grabX: point.x - element.rect.x,
          grabY: point.y - element.rect.y,
          rect: { ...element.rect },
        };
        group.alpha = 0.65;
        this.app.canvas.style.cursor = "grabbing";
        return;
      }

      event.stopPropagation();

      // La intención, igual que con una puerta: el servidor decide dónde te deja, y
      // `at` sólo le dice qué parte de este mueble señalaste.
      const donde = this.world.toLocal(event.global);
      this.room.send(Msg.UseElement, {
        elementId: element.id,
        at: { x: donde.x, y: donde.y },
      } satisfies UseElementPayload);
      this.markClick(donde);
    });

    return group;
  }

  /** Mueve el dibujo del mueble levantado y repinta su sombra. Sólo visual. */
  private dragTo(point: Point): void {
    const drag = this.dragging;
    if (!drag) return;

    const x = Math.round(point.x - drag.grabX);
    const y = Math.round(point.y - drag.grabY);
    const rect = { ...drag.rect, x, y };

    const view = this.propViews.get(drag.id)?.view;
    if (view) {
      view.x = x - drag.rect.x;
      view.y = y - drag.rect.y;
    }

    this.showDrop(rect, this.options.onDragValidate?.(rect, drag.id) ?? true);
  }

  /**
   * Suelta lo que se estuviera arrastrando.
   *
   * El stage NO decide si la nueva posición se guarda: manda el hecho hacia arriba y
   * vuelve a dibujar desde el dato. Si el servidor la rechaza, `setRoom` con la lista
   * de siempre devuelve la pieza a su sitio sola — que es exactamente el rebote que
   * queremos, sin guardar una posición "provisional" en ningún lado.
   */
  private dropDragged(clientX: number, clientY: number): void {
    const drag = this.dragging;
    if (!drag) return;

    this.dragging = null;
    this.app.canvas.style.cursor = "";
    this.showDrop(null, false);

    const view = this.propViews.get(drag.id)?.view;
    const moved = view ? { x: drag.rect.x + view.x, y: drag.rect.y + view.y } : drag.rect;

    /*
     * El dibujo vuelve a su sitio SIEMPRE, aunque el movimiento se acepte.
     *
     * `syncProps` no lo haría: compara una firma que sale del dato, y el dato todavía no
     * cambió —el desplazamiento vivía en el `view`, no en el elemento—. Sin esto, una
     * colocación rechazada dejaba la pieza dibujada donde la soltaste mientras el
     * servidor la seguía teniendo en su sitio de antes.
     */
    if (view) {
      view.x = 0;
      view.y = 0;
      view.alpha = 1;
    }

    this.options.onPieceDropped?.({
      placementId: drag.id,
      x: Math.round(moved.x),
      y: Math.round(moved.y),
      clientX,
      clientY,
    });
  }

  /** Los clubes del jugador local, tal como los sincroniza el servidor. */
  private myClubs(): string[] {
    return [...(this.room.state.players.get(this.room.sessionId)?.clubs ?? [])];
  }

  /**
   * El directorio de clubes: un tablón que se toca para abrir la lista.
   *
   * Antes acá se dibujaba un contenedor POR CLUB, pintado de su color. Dejó de poder
   * serlo cuando los clubes pasaron a fundarse: el mapa es dato fijo y no puede enumerar
   * algo que nace mientras el juego corre. Lo que se pierde —una fila de cajones de
   * colores, que se veía bien— se recupera en el escenario del patio, que sigue
   * dibujándolos de decorado.
   *
   * No comprueba nada de quién mira, y eso es deliberado: la lista la abre cualquiera,
   * porque es donde se pide entrar a un club del que todavía no eres. Quién puede ENTRAR
   * a una sede lo decide el servidor al unirse.
   */
  private buildClubDirectory(element: ElementOf<"directorio">, depth: number): Container {
    const group = new Container();
    const { x, y, width, height } = element.rect;

    const tablon = new Graphics();
    // El poste y el tablero: un panel de anuncios, no una puerta.
    tablon
      .rect(x + width / 2 - 7, y + height - 26, 14, 26)
      .fill({ color: 0x6b5336 })
      .rect(x, y, width, height - 22)
      .fill({ color: 0x2f4a63 })
      .stroke({ width: 3, color: 0x14304d });

    // Papeles clavados. Son decorado: lo que se lee de verdad sale en el panel de React.
    const papeles: [number, number, number, number][] = [
      [12, 12, 40, 26],
      [60, 14, 34, 22],
      [16, 46, 30, 20],
      [56, 44, 44, 26],
    ];
    for (const [dx, dy, w, h] of papeles) {
      if (dx + w > width - 8 || dy + h > height - 30) continue;
      tablon.rect(x + dx, y + dy, w, h).fill({ color: 0xf2ede0, alpha: 0.9 });
    }
    group.addChild(tablon);

    const rotulo = new Text({
      text: "CLUBES",
      style: {
        fontFamily: "Verdana, Geneva, sans-serif",
        fontSize: 12,
        fontWeight: "bold",
        fill: 0xffffff,
        stroke: { color: 0x0e2743, width: 4 },
      },
    });
    rotulo.anchor.set(0.5, 1);
    rotulo.x = x + width / 2;
    rotulo.y = y - 4;
    group.addChild(rotulo);

    group.zIndex = depth;
    group.eventMode = "static";
    group.cursor = "pointer";

    group.on("pointerdown", (event) => {
      if (this.editing) return;
      if (this.placing) return;
      event.stopPropagation();

      const donde = this.world.toLocal(event.global);
      this.room.send(Msg.UseElement, {
        elementId: element.id,
        at: { x: donde.x, y: donde.y },
      } satisfies UseElementPayload);
      this.markClick(donde);
    });

    return group;
  }

  /**
   * Rótulo de diagnóstico sobre el avatar, sólo para cuentas de desarrollo.
   *
   * Existe porque no hay arte de sentarse: sin un sprite distinto, alguien encima de
   * una silla se ve exactamente igual que alguien de pie a su lado, dieciséis píxeles
   * más abajo. Hasta que llegue el arte, esto es lo único que distingue las dos cosas.
   *
   * Se crea perezosamente: los jugadores normales no pagan ni un objeto de texto por
   * una ayuda que no van a ver.
   */
  private updateStateLabel(avatar: Avatar): void {
    if (!this.dev) return;

    const seated = avatar.player.onElement !== "";

    if (!avatar.state) {
      if (!seated) return;

      const text = new Text({
        text: "",
        style: {
          fontFamily: "Consolas, monospace",
          fontSize: 11,
          fill: 0xc77dff,
          stroke: { color: 0x000000, width: 3 },
        },
      });

      text.anchor.set(0.5, 1);
      // Por encima del nombre, que ya ocupa esa franja.
      text.y = -AVATAR_HEIGHT - 22;

      avatar.state = text;
      // Junto al nombre y por el mismo motivo: es información, no parte de la escena.
      avatar.view.nameplate.addChild(text);
    }

    avatar.state.visible = seated;
    if (seated) avatar.state.text = "sentado";
  }

/** Profundidad mínima para dibujarse por delante de un objeto, no detrás de él. */
  private frontOf(elementId: string): number {
    if (!elementId) return Number.NEGATIVE_INFINITY;

    /*
     * De la MISMA pila con la que se dibujó el objeto, no de su borde inferior.
     *
     * Derivarla aparte fue el bug de la tortuga sentada detrás de su propia silla: la
     * silla subió en la pila, su profundidad real subió con ella, y esta cuenta se quedó
     * en la de antes.
     */
    const depth = this.depths.get(elementId);
    if (depth !== undefined) return depth + 1;

    const element = this.def.elements.find((candidate) => candidate.id === elementId);
    return element ? element.rect.y + element.rect.height + 1 : Number.NEGATIVE_INFINITY;
  }

  /** Cuánto sube el sprite de quien está encima de este objeto. */
  private heightOf(elementId: string): number {
    if (!elementId) return 0;
    const element = this.def.elements.find((candidate) => candidate.id === elementId);
    return element?.surface?.height ?? 0;
  }

  private markClick(at: Point): void {
    this.clickAt = { x: at.x, y: at.y };
    this.clickAge = 0;
  }

  /** Anillo achatado que se expande y desvanece: lee como marca en el suelo. */
  private updateClickMarker(dt: number): void {
    if (this.clickAge >= CLICK_MARKER_SECONDS) {
      if (this.clickMarker.visible) this.clickMarker.visible = false;
      return;
    }

    this.clickAge += dt;

    const progress = Math.min(this.clickAge / CLICK_MARKER_SECONDS, 1);
    const radius = 5 + progress * 15;

    this.clickMarker.visible = true;
    this.clickMarker.clear();
    this.clickMarker
      .ellipse(this.clickAt.x, this.clickAt.y, radius, radius * 0.45)
      .stroke({ width: 2.5, color: 0xffd75e, alpha: 0.85 * (1 - progress) });
  }

  /**
   * Puertas ocultas: se cruzan al terminar de caminar dentro, sin clic.
   *
   * Se dispara en el FLANCO de caminando -> quieto, no mientras estás parado dentro.
   * La diferencia importa: el punto de llegada de otra sala puede caer dentro de una
   * puerta oculta, y comprobarlo de forma continua te haría rebotar entre las dos
   * salas para siempre. Al llegar por spawn no hay flanco, así que no dispara.
   */
  private resolveSilentDoor(): void {
    const me = this.avatars.get(this.room.sessionId);
    if (!me) return;

    const walking = me.player.walking;
    const stopped = this.wasWalking && !walking;
    this.wasWalking = walking;

    if (!stopped) return;

    const door = silentDoorAt(this.def, { x: me.player.x, y: me.player.y });
    if (door && this.canUse(door)) {
      this.room.send(Msg.UseElement, { elementId: door.id } satisfies UseElementPayload);
    }
  }

  /** Globo de diálogo sobre un avatar, reemplazando el que hubiera. */
  showBubble(sessionId: string, text: string): void {
    const avatar = this.avatars.get(sessionId);
    if (!avatar) return;

    clearTimeout(avatar.bubbleTimer);
    setBubble(avatar.view, text);

    avatar.bubbleTimer = setTimeout(() => {
      avatar.view.bubble.visible = false;
    }, BUBBLE_MS);
  }

  destroy(): void {
    this.destroyed = true;

    window.removeEventListener("keydown", this.onKeyDown);

    for (const avatar of this.avatars.values()) clearTimeout(avatar.bubbleTimer);
    this.avatars.clear();

    this.resizeObserver?.disconnect();
    // Al cruzar una puerta o cerrar un montaje tardío puede haber otro canvas vivo.
    // `true` también libera pools globales de Pixi y rompe los batches de ese canvas.
    this.app.destroy({ removeView: true, releaseGlobalResources: false }, { children: true });
  }
}
